import React, { useState, useRef, ChangeEvent, DragEvent } from "react";
import clsx from "clsx";
import { FileUploadSVGIcon } from "components/SVGIcons";
import styles from "./style.module.scss";

interface FileUploaderProps {
  isCarrierSubscribed: boolean;
  onFileSelect: (file: File) => void;
  compact?: boolean;
}

export default function FileUploader({
  isCarrierSubscribed,
  onFileSelect,
  compact = false
}: FileUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      onFileSelect(file);
      resetFileInput();
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileSelect(e.dataTransfer.files[0]);
      try {
        resetFileInput();
        e.dataTransfer.clearData();
      } catch (error) {
        // console.warn("Resetting the file input failed:", error);
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={clsx(
        styles.uploader,
        compact && styles.uploaderCompact,
        isCarrierSubscribed ? "flex-col gap-6 p-3 mt-5 border-dashed !border-2" : "",
        isDragging && styles.dragging
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onKeyDown={handleKeyDown}>
      <FileUploadSVGIcon className={compact ? "size-7 desktop:size-9" : "size-8 desktop:size-16"} />
      <span
        className={clsx(
          isCarrierSubscribed ? "text-subtitle" : "",
          compact && styles.uploaderTextCompact
        )}>
        {isCarrierSubscribed
          ? isDragging
            ? "Drop files here"
            : "Choose files to upload or drag and drop them here"
          : "Choose Files to upload"}
      </span>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        hidden
      />
    </div>
  );
}
